import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';

export type AdminRole = {
  id: number;
  name: 'admin' | 'super_admin' | 'delegate';
  description: string | null;
};

export type AdminPermission = {
  id: number;
  code: string;
  description: string | null;
  category: string | null;
};

export type AdminUser = {
  user_id: string;
  role: AdminRole;
  permissions: AdminPermission[];
  assigned_by: string | null;
  assigned_at: string;
};

export function useAdmin() {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Récupérer les informations d'administrateur de l'utilisateur connecté
  useEffect(() => {
    const fetchAdminInfo = async () => {
      if (!user) {
        setIsAdmin(false);
        setAdminRole(null);
        setPermissions([]);
        setLoading(false);
        return;
      }

      try {
        console.log('[useAdmin] Vérification si utilisateur est admin:', user.id);
        
        // 1. Vérifier d'abord si l'utilisateur est admin dans la table profiles
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('[useAdmin] Erreur lors de la vérification dans profiles:', profileError);
          setIsAdmin(false);
          setAdminRole(null);
          setPermissions([]);
          setLoading(false);
          return;
        }

        console.log('[useAdmin] Résultat de profiles:', profileData);
        
        if (!profileData || !profileData.is_admin) {
          console.log('[useAdmin] Utilisateur non admin selon la table profiles');
          setIsAdmin(false);
          setAdminRole(null);
          setPermissions([]);
          setLoading(false);
          return;
        }

        // 2. Récupérer les données de admin_users sans jointure
        console.log('[useAdmin] Récupération des données dans admin_users');
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('role_id, assigned_by, assigned_at')
          .eq('user_id', user.id)
          .single();

        if (adminError) {
          console.error('[useAdmin] Erreur lors de la récupération dans admin_users:', adminError);
          if (adminError.code !== 'PGRST116') { // Pas de lignes retournées
            throw adminError;
          }
          setIsAdmin(false);
          setAdminRole(null);
          setPermissions([]);
          setLoading(false);
          return;
        }

        console.log('[useAdmin] Données admin_users récupérées:', adminData);

        // 3. Récupérer les informations du rôle séparément
        console.log('[useAdmin] Récupération des informations du rôle');
        const { data: roleData, error: roleError } = await supabase
          .from('admin_roles')
          .select('id, name, description')
          .eq('id', adminData.role_id)
          .single();

        if (roleError) {
          console.error('[useAdmin] Erreur lors de la récupération du rôle:', roleError);
          throw roleError;
        }

        console.log('[useAdmin] Rôle récupéré:', roleData);

        // Récupérer les permissions de l'utilisateur
        console.log('[useAdmin] Récupération des permissions');
        const { data: permissionsData, error: permissionsError } = await supabase
          .from('admin_role_permissions')
          .select(`
            admin_permissions (
              id,
              code,
              description,
              category
            )
          `)
          .eq('role_id', adminData.role_id);

        if (permissionsError) {
          console.error('[useAdmin] Erreur lors de la récupération des permissions:', permissionsError);
          throw permissionsError;
        }

        console.log('[useAdmin] Permissions récupérées:', permissionsData);

        setIsAdmin(true);
        setAdminRole(roleData);
        setPermissions(permissionsData.map(p => p.admin_permissions[0] as AdminPermission));
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des informations d\'administrateur:', err);
        setError(err instanceof Error ? err : new Error('Erreur inconnue'));
        setIsAdmin(false);
        setAdminRole(null);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminInfo();
  }, [user]);

  // Vérifier si l'utilisateur a une permission spécifique
  const hasPermission = (code: string): boolean => {
    // Si l'utilisateur est super_admin, donner automatiquement accès à tout
    if (adminRole && adminRole.name === 'super_admin') {
      console.log(`[useAdmin] hasPermission: super_admin accès automatique à ${code}`);
      return true;
    }

    if (!permissions || permissions.length === 0) {
      console.log('[useAdmin] hasPermission: aucune permission disponible, retourne false pour', code);
      return false;
    }
    
    // Éviter l'erreur "Cannot read properties of undefined (reading 'code')"
    const hasAccess = permissions.some(p => p && p.code === code);
    console.log(`[useAdmin] hasPermission: vérification ${code} => ${hasAccess}`);
    return hasAccess;
  };

  // Récupérer tous les utilisateurs administrateurs
  const getAdminUsers = async (): Promise<{ data: AdminUser[] | null; error: Error | null }> => {
    try {
      if (!hasPermission('admins.manage')) {
        throw new Error('Permission non accordée pour gérer les administrateurs');
      }

      console.log('[useAdmin] Récupération de tous les admins');
      
      // Utiliser une approche simplifiée sans jointures complexes
      const { data: admins, error: adminsError } = await supabase
        .from('admin_users')
        .select('user_id, role_id, assigned_by, assigned_at');

      if (adminsError) {
        console.error('[useAdmin] Erreur lors de la récupération des admins:', adminsError);
        throw adminsError;
      }

      console.log('[useAdmin] Admins récupérés:', admins);
      
      // Récupérer les rôles séparément
      const { data: roles, error: rolesError } = await supabase
        .from('admin_roles')
        .select('id, name, description');
        
      if (rolesError) {
        console.error('[useAdmin] Erreur lors de la récupération des rôles:', rolesError);
        throw rolesError;
      }
      
      console.log('[useAdmin] Rôles récupérés:', roles);

      // Récupérer toutes les permissions
      const { data: allPermissions, error: permissionsError } = await supabase
        .from('admin_role_permissions')
        .select(`
          role_id,
          admin_permissions (
            id,
            code,
            description,
            category
          )
        `);

      if (permissionsError) {
        console.error('[useAdmin] Erreur lors de la récupération des permissions:', permissionsError);
        throw permissionsError;
      }
      
      console.log('[useAdmin] Permissions récupérées:', allPermissions);

      // Construire les objets AdminUser manuellement
      const adminUsers: AdminUser[] = [];
      
      for (const admin of admins) {
        // Trouver le rôle correspondant
        const role = roles.find(r => r.id === admin.role_id);
        
        if (!role) {
          console.warn('[useAdmin] Rôle non trouvé pour admin:', admin);
          continue;
        }
        
        // Trouver les permissions pour ce rôle
        const rolePermissions = allPermissions
          .filter(p => p.role_id === admin.role_id)
          .map(p => p.admin_permissions[0] as AdminPermission);
        
        adminUsers.push({
          user_id: admin.user_id,
          role: role as AdminRole,
          permissions: rolePermissions,
          assigned_by: admin.assigned_by,
          assigned_at: admin.assigned_at
        });
      }

      console.log('[useAdmin] AdminUsers construits:', adminUsers);
      
      return { data: adminUsers, error: null };
    } catch (err) {
      console.error('Erreur lors de la récupération des utilisateurs administrateurs:', err);
      return { data: null, error: err instanceof Error ? err : new Error('Erreur inconnue') };
    }
  };

  // Promouvoir un utilisateur en administrateur
  const promoteUser = async (userId: string, roleName: string): Promise<{ success: boolean; error: Error | null }> => {
    try {
      if (!hasPermission('admins.manage')) {
        throw new Error('Permission non accordée pour gérer les administrateurs');
      }

      if (!user) {
        throw new Error('Vous devez être connecté');
      }

      const { error } = await supabase
        .rpc('promote_to_admin', {
          target_user_id: userId,
          role_name: roleName,
          admin_user_id: user.id
        });

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (err) {
      console.error('Erreur lors de la promotion d\'un utilisateur:', err);
      return { success: false, error: err instanceof Error ? err : new Error('Erreur inconnue') };
    }
  };

  // Révoquer les droits d'administrateur d'un utilisateur
  const revokeAdmin = async (userId: string): Promise<{ success: boolean; error: Error | null }> => {
    try {
      if (!hasPermission('admins.manage')) {
        throw new Error('Permission non accordée pour gérer les administrateurs');
      }

      if (!user) {
        throw new Error('Vous devez être connecté');
      }

      const { error } = await supabase
        .rpc('revoke_admin', {
          target_user_id: userId,
          admin_user_id: user.id
        });

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (err) {
      console.error('Erreur lors de la révocation des droits d\'administrateur:', err);
      return { success: false, error: err instanceof Error ? err : new Error('Erreur inconnue') };
    }
  };

  // Obtenir les statistiques générales
  const getAdminStats = async (): Promise<{ data: any; error: Error | null }> => {
    try {
      console.log('[useAdmin] Tentative de récupération des statistiques');
      
      // Vérifier si l'utilisateur est admin
      if (!isAdmin) {
        console.log('[useAdmin] Utilisateur non admin, impossible de récupérer les statistiques');
        return { data: {}, error: new Error('Non autorisé') };
      }
      
      // Super admin a automatiquement accès à toutes les statistiques
      const isSuperAdmin = adminRole && adminRole.name === 'super_admin';
      
      // Vérifier que l'utilisateur a au moins une permission de visualisation
      const canViewUsers = isSuperAdmin || hasPermission('users.view');
      const canViewRelations = isSuperAdmin || hasPermission('relations.view');
      const canViewPayments = isSuperAdmin || hasPermission('payments.view');
      const canViewSettings = isSuperAdmin || hasPermission('settings.view');
      
      if (!canViewUsers && !canViewRelations && !canViewPayments && !canViewSettings) {
        console.log('[useAdmin] Aucune permission de visualisation');
        // Retourner un objet vide plutôt que null pour éviter les erreurs
        return { data: {}, error: new Error('Aucune permission de visualisation') };
      }

      // Préparer les statistiques à récupérer
      const stats: any = {};
      console.log('[useAdmin] Récupération des statistiques avec permissions:', {
        users: canViewUsers,
        relations: canViewRelations,
        payments: canViewPayments,
        settings: canViewSettings
      });

      // Nombre total d'utilisateurs
      if (canViewUsers) {
        try {
          const { count: usersCount, error: usersError } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true });

          if (usersError) {
            console.error('[useAdmin] Erreur lors de la récupération du nombre d\'utilisateurs:', usersError);
          } else {
            stats.totalUsers = usersCount;
          }
        } catch (err) {
          console.error('[useAdmin] Exception lors de la récupération du nombre d\'utilisateurs:', err);
        }
      }

      // Nombre total de relations
      if (canViewRelations) {
        try {
          const { count: relationsCount, error: relationsError } = await supabase
            .from('relations')
            .select('*', { count: 'exact', head: true });

          if (relationsError) {
            console.error('[useAdmin] Erreur lors de la récupération du nombre de relations:', relationsError);
          } else {
            stats.totalRelations = relationsCount;
          }
        } catch (err) {
          console.error('[useAdmin] Exception lors de la récupération du nombre de relations:', err);
        }
      }

      // Paiements totaux
      if (canViewPayments) {
        try {
          const { data: paymentsData, error: paymentsError } = await supabase
            .from('payment_logs')
            .select('amount, currency')
            .eq('status', 'completed');

          if (paymentsError) {
            console.error('[useAdmin] Erreur lors de la récupération des paiements:', paymentsError);
          } else {
            // Calculer le total par devise
            const paymentsByAmount: Record<string, number> = {};
            paymentsData.forEach(payment => {
              const key = payment.currency;
              paymentsByAmount[key] = (paymentsByAmount[key] || 0) + payment.amount;
            });
            
            stats.payments = {
              totalTransactions: paymentsData.length,
              byAmount: paymentsByAmount
            };
          }
        } catch (err) {
          console.error('[useAdmin] Exception lors de la récupération des paiements:', err);
        }
      }

      console.log('[useAdmin] Statistiques récupérées:', stats);
      return { data: stats, error: null };
    } catch (err) {
      console.error('[useAdmin] Erreur lors de la récupération des statistiques:', err);
      return { data: null, error: err instanceof Error ? err : new Error('Erreur inconnue') };
    }
  };

  return {
    isAdmin,
    adminRole,
    permissions,
    loading,
    error,
    hasPermission,
    getAdminUsers,
    promoteUser,
    revokeAdmin,
    getAdminStats
  };
} 